import { formatMinor } from '@hvac/contracts';
import { Alert, Button, Field, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import {
  answerQuestion,
  fileAnswer,
  questionPreviewQuery,
  requestAnswerUpload,
} from '../../shared/api/question-queries.js';
import { hashFile } from '../../shared/upload/hash.js';

/**
 * The client's side of a question: no sign-in, one screen, on a phone.
 *
 * It says who is asking and about which charge, then takes an answer in
 * words, a photograph of the receipt, or both. The photo goes straight to
 * storage and is registered as a document the way any upload is, so the
 * bookkeeper finds it in the Inbox already read.
 */
export function AnswerPage() {
  const { token } = useParams({ from: '/q/$token' });
  const preview = useQuery(questionPreviewQuery(token));
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  const send = useMutation({
    mutationFn: async () => {
      const who = name.trim() === '' ? null : name.trim();
      if (file === null) {
        await answerQuestion(token, text.trim(), who);
        return;
      }
      const mimeType = file.type === '' ? 'image/jpeg' : file.type;
      const ticket = await requestAnswerUpload(token, {
        filename: file.name === '' ? 'receipt.jpg' : file.name,
        mimeType,
        byteSize: file.size,
      });
      const put = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': mimeType, 'x-upsert': 'false' },
        body: file,
      });
      if (!put.ok) throw new Error(`The photo did not upload (${String(put.status)}). Try again.`);
      const { sha256 } = await hashFile(file, {});
      await fileAnswer(token, {
        objectKey: ticket.objectKey,
        filename: file.name === '' ? 'receipt.jpg' : file.name,
        mimeType,
        byteSize: file.size,
        sha256,
        answerText: text.trim() === '' ? null : text.trim(),
        answeredName: who,
      });
    },
    onSuccess: () => {
      setDone(true);
      setError(null);
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That did not go through. Try again.'),
  });

  const body = (() => {
    if (preview.isPending) return <Skeleton className="h-40 w-full" />;
    if (preview.isError) {
      return (
        <Alert tone="danger" title="This link is not recognised">
          It may have been copied incompletely. Ask whoever sent it for a new one.
        </Alert>
      );
    }
    const q = preview.data;
    if (done || q.status === 'answered') {
      return (
        <Alert tone="success" title="Thank you, that is filed">
          {q.businessName}&rsquo;s bookkeeper has it. Nothing else to do.
        </Alert>
      );
    }
    if (q.status !== 'open') {
      return (
        <Alert
          tone="warning"
          title={q.status === 'expired' ? 'This link has expired' : 'This question was withdrawn'}
        >
          Ask whoever sent it if they still need an answer.
        </Alert>
      );
    }
    return (
      <>
        <p className="text-sm text-content-muted">A question from</p>
        <h2 className="text-2xl font-semibold tracking-tight text-content">{q.businessName}</h2>
        {q.about === null ? null : (
          <p className="mt-3 rounded-[var(--radius-control)] bg-surface-sunken px-3 py-2 text-sm">
            <span className="font-medium">{formatMinor(q.about.amountMinor)}</span>{' '}
            {q.about.direction === 'out' ? 'paid' : 'received'} on {q.about.date}
            {q.about.description === null ? '' : `, "${q.about.description}"`}
            {q.about.account === null ? '' : ` (${q.about.account})`}
          </p>
        )}
        <p className="mt-4 text-base text-content">{q.prompt}</p>

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            send.mutate();
          }}
        >
          <Field label="Your answer" hint="A sentence is plenty.">
            {({ inputProps }) => (
              <textarea
                {...inputProps}
                rows={3}
                value={text}
                onChange={(event) => setText(event.target.value)}
                className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
              />
            )}
          </Field>

          <div className="flex flex-col gap-2">
            <Button type="button" variant="secondary" onClick={() => cameraInput.current?.click()}>
              {file === null
                ? 'Photograph the receipt'
                : `Photo attached: ${file.name === '' ? 'receipt' : file.name}`}
            </Button>
            {file === null ? null : (
              <button
                type="button"
                className="self-start text-xs text-content-muted hover:text-danger"
                onClick={() => setFile(null)}
              >
                Remove the photo
              </button>
            )}
            <input
              ref={cameraInput}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="sr-only"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
          </div>

          <Field label="Your name" hint="Optional. So the bookkeeper knows who answered.">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={name}
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <Button
            type="submit"
            isLoading={send.isPending}
            disabled={text.trim() === '' && file === null}
          >
            Send
          </Button>
        </form>
      </>
    );
  })();

  return (
    <main className="flex min-h-dvh items-start justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <p className="mb-8 flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]">
            P
          </span>
          <span className="text-sm font-semibold tracking-tight text-content">
            Pathara <span className="text-brand-600">Finance</span>
          </span>
        </p>
        {error === null ? null : (
          <div className="mb-4">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}
        {body}
      </div>
    </main>
  );
}
